import React, { useState } from 'react';
import Card from '../Card';
import Badge from '../Badge';
import { TopicExamplesList } from './TopicExamplesList';
import { TopicFrameworkTopic, TopicFrameworkSubtopic } from './types';

interface TopicFrameworkCardProps {
  topic: TopicFrameworkTopic;
  className?: string;
}

interface SubtopicItemProps {
  subtopic: TopicFrameworkSubtopic;
  isExpanded: boolean;
  onToggle: () => void;
}

const SubtopicItem: React.FC<SubtopicItemProps> = ({ subtopic, isExpanded, onToggle }) => {
  const getSubtopicBadgeColor = (type: string, sourceModule: string) => {
    if (sourceModule === 'module1') {
      return 'bg-green-50 text-green-700 border-green-200';
    }
    if (sourceModule === 'module2') {
      return 'bg-purple-50 text-purple-700 border-purple-200';
    }
    return 'bg-gray-50 text-gray-700 border-gray-200';
  };

  return (
    <div className="border-l-2 border-gray-200 pl-4 py-2">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h5 className="font-medium text-gray-900 dark:text-gray-100">
              {subtopic.name}
            </h5>
            <Badge
              text={subtopic.source_module}
              variant={subtopic.source_module === 'module1' ? 'success' : 'primary'}
              size="sm"
              className={getSubtopicBadgeColor(subtopic.type, subtopic.source_module)}
            />
            {subtopic.type.includes('semantic') && (
              <Badge
                text="semantic"
                variant="info"
                size="sm"
                className="bg-blue-50 text-blue-700 border-blue-200"
              />
            )}
          </div>
          
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            {subtopic.definition}
          </p>

          {subtopic.top_terms && subtopic.top_terms.length > 0 && (
            <div className="mb-2">
              <span className="text-xs font-medium text-gray-500 mr-2">Top Terms:</span>
              <div className="flex flex-wrap gap-1 inline">
                {subtopic.top_terms.map((term, index) => (
                  <Badge
                    key={index}
                    text={term}
                    variant="warning"
                    size="sm"
                    className="bg-orange-50 text-orange-700 border-orange-200"
                  />
                ))}
              </div>
            </div>
          )}

          {isExpanded && subtopic.examples && subtopic.examples.length > 0 && (
            <TopicExamplesList
              examples={subtopic.examples}
              title="Subtopic Examples"
              maxDisplay={5}
            />
          )}
        </div>
        
        {subtopic.examples && subtopic.examples.length > 0 && (
          <button
            onClick={onToggle}
            className="ml-2 text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
          >
            {isExpanded ? 'Hide Examples' : `Show Examples (${subtopic.examples.length})`}
          </button>
        )}
      </div>
    </div>
  );
};

export const TopicFrameworkCard: React.FC<TopicFrameworkCardProps> = ({
  topic,
  className = ""
}) => {
  const [expandedSubtopics, setExpandedSubtopics] = useState<Set<number>>(new Set());

  const toggleSubtopic = (index: number) => {
    const newExpanded = new Set(expandedSubtopics);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedSubtopics(newExpanded);
  };

  const getTopicBadgeColor = (sourceModule: string) => {
    if (sourceModule === 'module1') {
      return 'bg-green-100 text-green-800 border-green-300';
    }
    return 'bg-blue-100 text-blue-800 border-blue-300';
  };

  return (
    <Card className={`p-6 hover:shadow-lg transition-shadow duration-200 ${className}`}>
      <div className="space-y-4">
        {/* Topic Header */}
        <div className="border-b border-gray-200 pb-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {topic.name}
              </h3>
              <Badge
                text={`Topic #${topic.topic_id}`}
                variant={topic.source_module === 'module1' ? 'success' : 'primary'}
                size="sm"
                className={getTopicBadgeColor(topic.source_module)}
              />
            </div>
            {topic.type && (
              <Badge
                text={topic.type}
                variant="default"
                size="sm"
              />
            )}
          </div>
          
          <p className="text-gray-600 dark:text-gray-400 text-sm mb-3">
            {topic.definition}
          </p>

          {topic.entity_type && (
            <div className="mb-3">
              <Badge
                text={`Entity Type: ${topic.entity_type}`}
                variant="warning"
                size="sm"
                className="bg-yellow-50 text-yellow-700 border-yellow-200 mr-2"
              />
              {topic.top_entities && topic.top_entities.length > 0 && (
                <span className="text-xs text-gray-500">
                  Top entities: {topic.top_entities.join(', ')}
                </span>
              )}
            </div>
          )}

          {/* Topic Examples */}
          {topic.examples && topic.examples.length > 0 && (
            <TopicExamplesList
              examples={topic.examples}
              title="Topic Examples"
              maxDisplay={6}
            />
          )}
        </div>

        {/* Subtopics */}
        {topic.subtopics && topic.subtopics.length > 0 && (
          <div className="space-y-3">
            <h4 className="font-medium text-gray-900 dark:text-gray-100">
              Subtopics ({topic.subtopics.length})
            </h4>
            <div className="space-y-3">
              {topic.subtopics.map((subtopic, index) => (
                <SubtopicItem
                  key={index}
                  subtopic={subtopic}
                  isExpanded={expandedSubtopics.has(index)}
                  onToggle={() => toggleSubtopic(index)}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};